import { FormSkeleton, UserPermission } from '@globaleris/ui.library'
import {
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumCommodity, EnumTipoSujeto } from '../../api'
import ButtonWithPermissions from '../../components/buttonWithPermissions/buttonWithPermissions'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import { ButtonOption, SplitButton } from '../../components/shared/splitButton/splitButton'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { enumEnumToValueNumber, enumToMapValues, enumValueToKey, MapToArray } from '../../helpers'
import { hasPermission, hasResource } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../model'
import { Resources } from '../../model/RBAC/constants'
import {
  getActiveCommodities,
  getActiveCommodity,
  storeActiveCommodity,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'
import { selectActiveCompany } from '../../redux/selectors/profile.selectors'
import { fetchListAnnouncement, fetchListKpis } from './client.service'
import { styles } from './clientes.styles'
import { LandingSujectKpisModel, SubjectAnnouncementModel, TabsClientType } from './clients.model'
import AnnouncementList from './components/AnnouncementList'
import KPIList from './components/KPIList'

export interface ClientPageProps {}

export const ClientPage: React.FC<ClientPageProps> = props => {
  let history = useHistory()
  const [t] = useTranslation(['globalerisLibrary', 'clients', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const responsive = useMediaQuery(currentTheme.breakpoints.down('xs'))
  const responsiveDivider = useMediaQuery(currentTheme.breakpoints.down('md'))

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  const { activeTab } = usePageState()
  const commodities = getActiveCommodities()
  const activeCompany = useSelector(selectActiveCompany)

  const hasClientOrExtendedEditPermission =
    hasPermission(Resources.Client, [UserPermission.EDIT]) ||
    hasPermission(Resources.ExtendedClients, [UserPermission.EDIT])

  const hasClientOrExtendedEditReadOnlyPermission =
    hasPermission(Resources.Client, [UserPermission.EDIT, UserPermission.READ_ONLY]) ||
    hasPermission(Resources.ExtendedClients, [UserPermission.EDIT, UserPermission.READ_ONLY])

  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (commodities !== undefined && commodities?.length > 0
      ? enumValueToKey(EnumCommodity, commodities[0])
      : EnumCommodity.GasNatural)

  const [isFetchingAnnouncement, setIsFetchingAnnouncement] = useState<boolean>(false)
  const [isFetchingKpis, setIsFetchingKpis] = useState<boolean>(false)
  const [currentTab, setCurrentTab] = useState<number>(0)
  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const [listAnnouncement, setListAnnouncement] = useState<SubjectAnnouncementModel | undefined>(
    undefined
  )
  const [listKpis, setListKpis] = useState<LandingSujectKpisModel | undefined>(undefined)

  // We set an Array from the ClientTypes in order to have arranged both index (table tabs index, starting in 0) and values corresponding to each each clientType.
  const ClientTabsArray = MapToArray(enumToMapValues(TabsClientType))

  const filteredTabs = ClientTabsArray.filter(tab => {
    if (tab.value === TabsClientType.FINAL_CLIENT) {
      return hasResource(Resources.ExtendedClients) ? false : true
    } else if (tab.value === TabsClientType.EXTENDED_CLIENT) {
      return hasResource(Resources.ExtendedClients) ? true : false
    } else return true
  })

  // The tabs component indexes only the tabs it renders (those with permission), so we keep only
  // the permitted ones to make the tab index match the tab id lookups.
  const permittedTabs: Tab[] = filteredTabs
    .map(clientType => ({
      id: clientType.value,
      label: t(`clients:landingPage.tabs.${clientType.name}`),
      hasPermission:
        hasClientOrExtendedEditReadOnlyPermission &&
        (clientType.value === TabsClientType.COMMERCIAL
          ? hasResource(Resources.Commercial)
          : true) &&
        (clientType.value === TabsClientType.WHOLESALER ? hasResource(Resources.Trades) : true) &&
        (clientType.value === TabsClientType.SHIPPER ||
        clientType.value === TabsClientType.DISTRIBUTION
          ? hasResource(Resources.Administration)
          : true)
    }))
    .filter(tab => tab.hasPermission)

  const tabsDefinition: Map<number, Tab> = new Map(
    permittedTabs.map((tab, tabIndex) => [tabIndex, tab])
  )

  // Hooks Region Starts

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  useEffect(() => {
    const getListAnnouncement = async () => {
      setIsFetchingAnnouncement(true)
      await fetchListAnnouncement(getSubjectTypeByCurrentTab(currentTab), commodity).then(
        response => {
          setListAnnouncement(response.data)
          setIsFetchingAnnouncement(false)
        }
      )
    }

    const getListKpis = async () => {
      setIsFetchingKpis(true)
      await fetchListKpis(getSubjectTypeByCurrentTab(currentTab), commodity).then(response => {
        setListKpis(response.data)
        setIsFetchingKpis(false)
      })
    }

    getListAnnouncement()
    getListKpis()
  }, [currentTab, commodity, activeCompany])

  // Hooks Region Ends

  // Functions Region Starts

  const getSubjectTypeByCurrentTab = (tabIdx: number): EnumTipoSujeto => {
    const tab = tabsDefinition.get(tabIdx)

    let tipoSujeto: EnumTipoSujeto = EnumTipoSujeto.ClienteFinal

    switch (tab?.id) {
      case enumEnumToValueNumber(TabsClientType, TabsClientType.WHOLESALER): {
        tipoSujeto = EnumTipoSujeto.Mayorista
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.COMMERCIAL): {
        tipoSujeto = EnumTipoSujeto.Comercial
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.EXTENDED_CLIENT): {
        tipoSujeto = EnumTipoSujeto.ClienteExtendido
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.SHIPPER): {
        tipoSujeto = EnumTipoSujeto.Transportista
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.DISTRIBUTION): {
        tipoSujeto = EnumTipoSujeto.Distribuidor
        break
      }
      default: {
        tipoSujeto = EnumTipoSujeto.ClienteFinal
      }
    }

    return tipoSujeto
  }

  // Handlers Region Starts

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity(commodity)
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
  }

  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)
      setGlobalNotification(undefined)
    }
  }

  const onConceptClickHandler = async () => {
    const tab = tabsDefinition.get(currentTab)
    if (!tab) {
      return
    }
    // New Concept Page
    history.push(NonAuthRoutes.Concepts, { clientType: tab.id })
  }

  const onProductClickHandler = async () => {
    const tab = tabsDefinition.get(currentTab)
    if (!tab) {
      return
    }
    // New Product Page
    history.push(NonAuthRoutes.Products, { clientType: tab.id })
  }

  const onSubjectListClickHandler = async () => {
    const tab = tabsDefinition.get(currentTab)
    if (!tab) {
      return
    }

    switch (tab.id) {
      case enumEnumToValueNumber(TabsClientType, TabsClientType.WHOLESALER): {
        history.push(NonAuthRoutes.partners)
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.COMMERCIAL): {
        history.push(NonAuthRoutes.Commercial)
        break
      }
      default: {
        history.push(NonAuthRoutes.clientsMod)
      }
    }
  }

  const onSearchSubjectClickHandler = async () => {
    const tab = tabsDefinition.get(currentTab)
    if (!tab) {
      return
    }

    switch (tab.id) {
      case enumEnumToValueNumber(TabsClientType, TabsClientType.WHOLESALER): {
        history.push(NonAuthRoutes.ClientSearch, { subjectType: TabsClientType.WHOLESALER })
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.COMMERCIAL): {
        history.push(NonAuthRoutes.ClientSearch, { subjectType: TabsClientType.COMMERCIAL })
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.EXTENDED_CLIENT): {
        history.push(NonAuthRoutes.ClientSearch, { subjectType: TabsClientType.EXTENDED_CLIENT })
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.SHIPPER): {
        history.push(NonAuthRoutes.ClientSearch, { subjectType: TabsClientType.SHIPPER })
        break
      }
      case enumEnumToValueNumber(TabsClientType, TabsClientType.DISTRIBUTION): {
        history.push(NonAuthRoutes.ClientSearch, { subjectType: TabsClientType.DISTRIBUTION })
        break
      }
      default: {
        history.push(NonAuthRoutes.ClientSearch)
      }
    }
  }

  const onAddSubjectClickHandler = async (selectedOption: ButtonOption) => {
    if (selectedOption.id === 0) {
      history.push(NonAuthRoutes.clientsModAdd)
    } else if (selectedOption.id === 1) {
      let tipoSujeto = getSubjectTypeByCurrentTab(currentTab)
      history.push(NonAuthRoutes.ClientAdd, { subjectType: tipoSujeto })
    }
  }

  const isAddSubjectBtnDisabled = () => {
    const tab = tabsDefinition.get(currentTab)
    if (tab) {
      // Se habilita Crear Mayorista para Test
      // return tab.id === enumEnumToValueNumber(TabsClientType, TabsClientType.WHOLESALER)
    }

    return false
  }

  const getAddButtons = (): ButtonOption[] => {
    const tab = tabsDefinition.get(currentTab)
    if (
      tab &&
      (tab.id === enumEnumToValueNumber(TabsClientType, TabsClientType.WHOLESALER) ||
        tab.id === enumEnumToValueNumber(TabsClientType, TabsClientType.EXTENDED_CLIENT))
    ) {
      // No existe crear cliente Mayorista ni Cliente Extendido en Front MVC
      return [{ id: 1, name: 'standard', label: t('clients:landingPage.buttons.addSubjectStd') }]
    }

    return [
      { id: 0, name: 'default', label: t('clients:landingPage.buttons.addSubject') },
      { id: 1, name: 'standard', label: t('clients:landingPage.buttons.addSubjectStd') }
    ] as ButtonOption[]
  }

  // Handlers Region Ends

  const renderTabContent = (activeTab: number) => {
    return (
      getSubjectTypeByCurrentTab(activeTab) && (
        <Card>
          <CardContent>
            <Grid container>
              <Grid
                container
                item
                xs={12}
                lg={!hasClientOrExtendedEditPermission ? 12 : 2}
                className={classes.titleContainer}
                style={{
                  marginRight: responsive ? 0 : currentTheme.spacing(4)
                }}
              >
                <Grid item xs={12}>
                  <Typography
                    style={{
                      marginLeft: responsive ? 'auto' : currentTheme.spacing(2),
                      textAlign: responsive ? 'center' : 'left'
                    }}
                  >
                    {t('clients:landingPage.kpis.title')}
                  </Typography>
                </Grid>
                {!isFetchingKpis && listKpis ? (
                  <KPIList KPIItems={listKpis} />
                ) : (
                  <Grid container item xs={12} justify={'center'}>
                    <FormSkeleton elevation={0} columns={3} inputs={1}></FormSkeleton>
                  </Grid>
                )}
              </Grid>
              <Divider
                className={responsiveDivider ? classes.dividerHorizontal : classes.dividerVertical}
                orientation="vertical"
                flexItem
              ></Divider>
              <Grid
                item
                xs={12}
                lg={!hasClientOrExtendedEditPermission ? 12 : 9}
                className={classes.titleContainer}
                style={{
                  marginTop: responsiveDivider ? currentTheme.spacing(2) : '0'
                }}
              >
                <Typography
                  style={{
                    marginLeft: responsive ? 'auto' : currentTheme.spacing(2),
                    textAlign: responsive ? 'center' : 'left'
                  }}
                >
                  {t('clients:landingPage.announcement.title')}
                </Typography>
                {!isFetchingAnnouncement && listAnnouncement ? (
                  <AnnouncementList
                    announcementItems={listAnnouncement}
                    subjectType={getSubjectTypeByCurrentTab(currentTab)}
                  />
                ) : (
                  <FormSkeleton elevation={0} columns={1} inputs={1}></FormSkeleton>
                )}
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={responsive ? classes.parentFlexColumn : classes.parentFlexRight}>
            <ButtonWithPermissions
              onClick={onConceptClickHandler}
              size="small"
              color="primary"
              hasPermission={hasClientOrExtendedEditReadOnlyPermission}
            >
              {t('clients:landingPage.buttons.concepts')}
            </ButtonWithPermissions>
            <ButtonWithPermissions
              onClick={onProductClickHandler}
              size="small"
              color="primary"
              hasPermission={hasClientOrExtendedEditReadOnlyPermission}
            >
              {t('clients:landingPage.buttons.products')}
            </ButtonWithPermissions>
            {/* <ButtonWithPermissions
              onClick={onProductNewClickHandler}
              size="small"
              color="primary"
              hasPermission={hasPermission(Resources.Client, [
                UserPermission.EDIT,
                UserPermission.READ_ONLY
              ])}
            >
              Productos Nuevo
            </ButtonWithPermissions> */}
            <ButtonWithPermissions
              onClick={onSubjectListClickHandler}
              size="small"
              color="primary"
              hasPermission={hasClientOrExtendedEditReadOnlyPermission}
            >
              {t('clients:landingPage.buttons.subjectList')}
            </ButtonWithPermissions>
            <ButtonWithPermissions
              onClick={onSearchSubjectClickHandler}
              size="small"
              color="primary"
              hasPermission={hasClientOrExtendedEditReadOnlyPermission}
            >
              {t('clients:landingPage.buttons.search')}
            </ButtonWithPermissions>
            <SplitButton
              options={getAddButtons()}
              onClick={onAddSubjectClickHandler}
              disabled={isAddSubjectBtnDisabled()}
              hasPermission={hasClientOrExtendedEditPermission}
            />
            {/* <ButtonWithPermissions
              variant="contained"
              onClick={onAddSubjectClickHandler}
              size="small"
              color="primary"
              disabled={isAddSubjectBtnDisabled()}
              hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
            >
              {t('clients:landingPage.buttons.addSubject')}
            </ButtonWithPermissions> */}
          </CardActions>
        </Card>
      )
    )
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!(isFetchingAnnouncement || isFetchingKpis)}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  // Functions Region Ends

  return (
    <Page notification={globalNotification}>
      {commodities && (
        <>
          <PageHeader title={t('clients:titleLanding')}>
            <div>
              <Grid container justify="flex-end">
                {renderCommodities()}
              </Grid>
            </div>
          </PageHeader>
          <TabsWithPermissions
            tabs={tabsDefinition}
            tabDefault={currentTab}
            onTabChange={onTabChangeHandler}
          ></TabsWithPermissions>
          {renderTabContent(activeTab ? activeTab : currentTab)}
        </>
      )}
    </Page>
  )
}
